import { ownerStorage } from '../lib/privacy/ownerStorage';
import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, WifiOff } from 'lucide-react';

/**
 * Error type classification for better handling
 */
type ErrorType = 'network' | 'chunk_load' | 'runtime' | 'unknown';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Feature name for logging and display */
  feature?: string;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Whether to show simplified fallback for feature errors */
  minimal?: boolean;
  /** Auto-retry for transient errors */
  autoRetry?: boolean;
  /** Max auto-retry attempts */
  maxRetries?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorType: ErrorType;
  retryCount: number;
  isOnline: boolean;
}

/**
 * Classify error type for appropriate handling
 */
function classifyError(error: Error): ErrorType {
  const message = error.message.toLowerCase();

  // Network errors
  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('failed to load') ||
    message.includes('networkerror')
  ) {
    return 'network';
  }

  // Chunk loading errors (code splitting)
  if (
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module')
  ) {
    return 'chunk_load';
  }

  // Runtime errors
  if (
    message.includes('cannot read') ||
    message.includes('undefined') ||
    message.includes('null')
  ) {
    return 'runtime';
  }

  return 'unknown';
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorType: 'unknown',
      retryCount: 0,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorType: classifyError(error),
    };
  }

  componentDidMount() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { feature, onError, autoRetry, maxRetries = 3 } = this.props;
    const { retryCount, errorType } = this.state;

    // Log to console
    console.error(
      `[ErrorBoundary${feature ? `:${feature}` : ''}] Error caught:`,
      error,
      errorInfo
    );

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Track error event (if tracking is available)
    try {
      const errorEvent = {
        type: 'error_boundary_triggered',
        feature: feature || 'app',
        errorType,
        message: error.message,
        stack: error.stack?.substring(0, 500), // Truncate stack
        timestamp: new Date().toISOString(),
      };

      // Store in localStorage for later sync
      const errors = JSON.parse(ownerStorage.getItem('resilience_errors') || '[]');
      errors.push(errorEvent);
      // Keep only last 50 errors
      if (errors.length > 50) errors.shift();
      ownerStorage.setItem('resilience_errors', JSON.stringify(errors));
    } catch {
      // Silent fail - don't cause more errors
    }

    // Auto-retry for transient errors
    if (
      autoRetry &&
      retryCount < maxRetries &&
      (errorType === 'network' || errorType === 'chunk_load')
    ) {
      const delay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff
      this.retryTimeoutId = window.setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          error: null,
          retryCount: prev.retryCount + 1,
        }));
      }, delay);
    }
  }

  handleOnline = () => {
    this.setState({ isOnline: true });
    // Auto-retry on reconnect if there was a network error
    if (this.state.hasError && this.state.errorType === 'network') {
      this.handleReset();
    }
  };

  handleOffline = () => {
    this.setState({ isOnline: false });
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
    window.location.href = '/';
  };

  /**
   * Get error-specific messaging
   */
  getErrorContent() {
    const { errorType, isOnline, retryCount } = this.state;
    const { feature, maxRetries = 3 } = this.props;

    // Network/offline errors
    if (!isOnline || errorType === 'network') {
      return {
        icon: <WifiOff size={32} style={{ color: 'var(--bg-void)' }} />,
        iconBg: 'var(--accent-warning)',
        title: 'Connection Lost',
        message: "You're currently offline. Your progress is saved locally and will sync when you reconnect.",
        showRetry: true,
      };
    }

    // Chunk loading errors (code splitting)
    if (errorType === 'chunk_load') {
      return {
        icon: <RefreshCw size={32} style={{ color: 'var(--bg-void)' }} />,
        iconBg: 'var(--accent-calm)',
        title: 'Update Available',
        message: 'A newer version is available. Please refresh to continue.',
        showRetry: true,
      };
    }

    // Auto-retry in progress
    if (retryCount > 0 && retryCount < maxRetries) {
      return {
        icon: <RefreshCw size={32} style={{ color: 'var(--bg-void)' }} />,
        iconBg: 'var(--accent-calm)',
        title: 'Retrying...',
        message: `Attempting to recover (${retryCount}/${maxRetries})...`,
        showRetry: false,
      };
    }

    // Feature-specific errors
    if (feature) {
      return {
        icon: <AlertTriangle size={32} style={{ color: 'var(--bg-void)' }} />,
        iconBg: 'var(--accent-stress)',
        title: `${feature} Unavailable`,
        message: `This feature encountered an issue. Your other progress is safe.`,
        showRetry: true,
      };
    }

    // Generic error
    return {
      icon: <AlertTriangle size={32} style={{ color: 'var(--bg-void)' }} />,
      iconBg: 'var(--accent-stress)',
      title: 'Something Went Wrong',
      message: "Don't worry - your progress is safe. Try refreshing the page or going back to the home screen.",
      showRetry: true,
    };
  }

  render() {
    const { hasError, isOnline } = this.state;
    const { fallback, minimal, feature } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      const content = this.getErrorContent();

      // Minimal fallback for feature-level errors
      if (minimal) {
        return (
          <div
            style={{
              padding: 'var(--space-lg)',
              textAlign: 'center',
              background: 'var(--bg-elevated)',
              borderRadius: '12px',
              border: '2px solid var(--border-subtle)',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                margin: '0 auto var(--space-md)',
                background: content.iconBg,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {content.icon}
            </div>
            <p
              style={{
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
                marginBottom: 'var(--space-md)',
              }}
            >
              {feature || 'This section'} is temporarily unavailable
            </p>
            {content.showRetry && (
              <button
                onClick={this.handleReset}
                style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  background: 'var(--accent-calm)',
                  border: '2px solid var(--border-harsh)',
                  borderRadius: '8px',
                  color: 'var(--bg-void)',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Try Again
              </button>
            )}
          </div>
        );
      }

      // Full-page error UI
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-lg)',
            background: 'var(--bg-void)',
          }}
        >
          {/* Offline banner */}
          {!isOnline && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                padding: 'var(--space-sm) var(--space-md)',
                background: 'var(--accent-warning)',
                color: 'var(--bg-void)',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-sm)',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              <WifiOff size={16} />
              You're offline - changes will sync when reconnected
            </div>
          )}

          <div
            style={{
              maxWidth: '400px',
              textAlign: 'center',
              padding: 'var(--space-xl)',
              background: 'var(--bg-surface)',
              borderRadius: '16px',
              border: '2px solid var(--border-harsh)',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                margin: '0 auto var(--space-lg)',
                background: content.iconBg,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {content.icon}
            </div>

            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.5rem',
                marginBottom: 'var(--space-sm)',
                color: 'var(--text-primary)',
              }}
            >
              {content.title}
            </h2>

            <p
              style={{
                color: 'var(--text-muted)',
                marginBottom: 'var(--space-lg)',
                fontSize: '0.875rem',
              }}
            >
              {content.message}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div
                style={{
                  padding: 'var(--space-md)',
                  background: 'var(--bg-elevated)',
                  borderRadius: '8px',
                  marginBottom: 'var(--space-lg)',
                  textAlign: 'left',
                  overflow: 'auto',
                }}
              >
                <p
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'var(--accent-stress)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.message}
                </p>
              </div>
            )}

            {content.showRetry && (
              <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
                <button
                  onClick={this.handleReset}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-xs)',
                    padding: 'var(--space-md)',
                    background: 'var(--accent-calm)',
                    border: '2px solid var(--border-harsh)',
                    borderRadius: '8px',
                    color: 'var(--bg-void)',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 0.15s',
                  }}
                >
                  <RefreshCw size={18} />
                  {isOnline ? 'Try Again' : 'Retry When Online'}
                </button>

                <button
                  onClick={this.handleGoHome}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-xs)',
                    padding: 'var(--space-md)',
                    background: 'var(--bg-elevated)',
                    border: '2px solid var(--border-harsh)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 0.15s',
                  }}
                >
                  <Home size={18} />
                  Go Home
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Feature-specific error boundary with minimal UI
 * Use for wrapping individual features that can gracefully degrade
 */
export function FeatureErrorBoundary({
  children,
  feature,
  fallback,
}: {
  children: ReactNode;
  feature: string;
  fallback?: ReactNode;
}) {
  return (
    <ErrorBoundary
      feature={feature}
      minimal
      autoRetry
      maxRetries={2}
      fallback={fallback}
    >
      {children}
    </ErrorBoundary>
  );
}

